import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import T2TTopNav from "../Layout/T2TTopNav";
import { getData } from "../../../FetchApiServices";
import T2TVendorActions, { T2TDocExpiryBadge, T2TKycBadge } from "./T2TVendorActions";
import T2TVerificationPanel from "./T2TVerificationPanel";
import T2TSapPushPanel from "./T2TSapPushPanel";
import { T2TVendorInviteButton } from "./T2TVendorPortal";
import {
  listVendorDocs, deleteVendorDoc, getDocDownloadUrl, registerVendorDoc, presignVendorDoc,
  listVendorAddresses, createVendorAddress, updateVendorAddress, deleteVendorAddress,
  listVendorBankAccounts, createVendorBankAccount, updateVendorBankAccount, deleteVendorBankAccount,
  listVendorHistory,
} from "../../../api/t2tVendorChildApi";

const STATUS_COLORS = {
  active:        { bg: "#F0FDF4", color: "#16A34A" },
  blocked:       { bg: "#FEF2F2", color: "#DC2626" },
  deactivated:   { bg: "#F8FAFC", color: "#64748B" },
  pending_rekyc: { bg: "#FFF7ED", color: "#D97706" },
};

const CARD = { background: "white", border: "1px solid #E2E8F0", borderRadius: 8, padding: 16 };
const EMPTY = { color: "#94A3B8", fontSize: 13, padding: 20 };

// ─── Address form modal ────────────────────────────────────────────────────────
function AddressModal({ vendorId, existing, onSaved, onClose }) {
  const [form, setForm] = useState(existing || { address_type: "registered", street: "", city: "", state: "", postal_code: "", country_code: "IN", phone: "", email: "", is_primary: 0 });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    const fn = existing ? updateVendorAddress(vendorId, existing.address_id, form) : createVendorAddress(vendorId, form);
    const resp = await fn;
    setSaving(false);
    if (!resp.error) { onSaved(); onClose(); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "white", borderRadius: 10, width: 440, padding: 24, boxShadow: "0 10px 40px rgba(0,0,0,.2)" }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>{existing ? "Edit Address" : "Add Address"}</div>
        {[
          ["Address Type", "address_type", "text"],
          ["Street",       "street",       "text"],
          ["City",         "city",         "text"],
          ["State",        "state",        "text"],
          ["Postal Code",  "postal_code",  "text"],
          ["Country Code", "country_code", "text"],
          ["Phone",        "phone",        "text"],
          ["Email",        "email",        "email"],
        ].map(([label, key, type]) => (
          <div key={key} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", marginBottom: 3 }}>{label}</div>
            <input className="form-control form-control-sm" type={type} value={form[key] || ""} onChange={set(key)} />
          </div>
        ))}
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginBottom: 14 }}>
          <input type="checkbox" checked={!!form.is_primary} onChange={(e) => setForm((f) => ({ ...f, is_primary: e.target.checked ? 1 : 0 }))} />
          Mark as Primary
        </label>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-sm btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

AddressModal.propTypes = {
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  existing: PropTypes.object,
  onSaved:  PropTypes.func.isRequired,
  onClose:  PropTypes.func.isRequired,
};

// ─── Bank Account form modal ───────────────────────────────────────────────────
function BankModal({ vendorId, existing, onSaved, onClose }) {
  const [form, setForm] = useState(existing || { bank_name: "", account_number: "", ifsc_code: "", swift_code: "", iban: "", account_type: "current", currency: "INR", is_primary: 0 });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    const fn = existing ? updateVendorBankAccount(vendorId, existing.bank_account_id, form) : createVendorBankAccount(vendorId, form);
    const resp = await fn;
    setSaving(false);
    if (!resp.error) { onSaved(); onClose(); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "white", borderRadius: 10, width: 440, padding: 24, boxShadow: "0 10px 40px rgba(0,0,0,.2)" }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>{existing ? "Edit Bank Account" : "Add Bank Account"}</div>
        {[
          ["Bank Name",       "bank_name",      "text"],
          ["Account Number",  "account_number", "text"],
          ["IFSC Code",       "ifsc_code",      "text"],
          ["SWIFT Code",      "swift_code",     "text"],
          ["IBAN",            "iban",           "text"],
          ["Account Type",    "account_type",   "text"],
          ["Currency",        "currency",       "text"],
        ].map(([label, key, type]) => (
          <div key={key} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", marginBottom: 3 }}>{label}</div>
            <input className="form-control form-control-sm" type={type} value={form[key] || ""} onChange={set(key)} />
          </div>
        ))}
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginBottom: 14 }}>
          <input type="checkbox" checked={!!form.is_primary} onChange={(e) => setForm((f) => ({ ...f, is_primary: e.target.checked ? 1 : 0 }))} />
          Mark as Primary
        </label>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-sm btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

BankModal.propTypes = {
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  existing: PropTypes.object,
  onSaved:  PropTypes.func.isRequired,
  onClose:  PropTypes.func.isRequired,
};

// ─── Document upload modal ─────────────────────────────────────────────────────
function DocUploadModal({ vendorId, onSaved, onClose }) {
  const [docType,   setDocType]   = useState("PAN");
  const [expiry,    setExpiry]    = useState("");
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState("");
  const fileRef = useRef(null);

  const upload = async () => {
    if (!file) { setError("Select a file first"); return; }
    setUploading(true);
    setError("");
    try {
      // 1. Get presigned URL
      const presignResp = await presignVendorDoc(vendorId, {
        filename: file.name, mime_type: file.type, document_type: docType,
      });
      if (presignResp.error) throw new Error(presignResp.error?.message || "Presign failed");

      const { presigned_url, storage_key } = presignResp.data?.data || presignResp.data || {};

      // 2. Upload to S3
      const s3Resp = await fetch(presigned_url, {
        method: "PUT", body: file, headers: { "Content-Type": file.type },
      });
      if (!s3Resp.ok) throw new Error("S3 upload failed");

      // 3. Register in DB (no file_url column — use storage_key as the reference)
      const regResp = await registerVendorDoc(vendorId, {
        document_type: docType,
        document_name: file.name,
        storage_key,
        mime_type: file.type,
        file_size_bytes: file.size,
        expiry_date: expiry || null,
      });
      if (regResp.error) throw new Error(regResp.error?.message || "Register failed");

      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    }
    setUploading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "white", borderRadius: 10, width: 400, padding: 24, boxShadow: "0 10px 40px rgba(0,0,0,.2)" }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Upload Document</div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", marginBottom: 3 }}>Document Type</div>
          <select className="form-select form-select-sm" value={docType} onChange={(e) => setDocType(e.target.value)}>
            {["PAN","GST","MSME","Cancelled Cheque","Incorporation Certificate","Trade License","Other"].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", marginBottom: 3 }}>Expiry Date (optional)</div>
          <input className="form-control form-control-sm" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", marginBottom: 3 }}>File</div>
          <input ref={fileRef} type="file" className="form-control form-control-sm"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png"
            onChange={(e) => setFile(e.target.files[0] || null)} />
        </div>

        {error && <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 10 }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="btn btn-sm btn-primary" disabled={uploading} onClick={upload}>
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}

DocUploadModal.propTypes = {
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSaved:  PropTypes.func.isRequired,
  onClose:  PropTypes.func.isRequired,
};

// ─── Main component ────────────────────────────────────────────────────────────
const T2TVendorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [addresses, setAddresses] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [history, setHistory] = useState([]);
  const [relatedRequests, setRelatedRequests] = useState([]);
  const [performance, setPerformance] = useState(null);

  // modal state
  const [addrModal, setAddrModal]   = useState(null); // null | "new" | {address obj}
  const [bankModal, setBankModal]   = useState(null);
  const [docModal,  setDocModal]    = useState(false);

  const fetchVendor = async () => {
    setLoading(true);
    const resp = await getData(`t2t/vendors/${id}`);
    if (!resp.error) setVendor(resp.data?.data);
    setLoading(false);
  };

  const reloadAddresses    = () => listVendorAddresses(id).then((r)  => { if (!r.error) setAddresses(r.data?.data   || []); });
  const reloadBankAccounts = () => listVendorBankAccounts(id).then((r) => { if (!r.error) setBankAccounts(r.data?.data || []); });
  const reloadDocs         = () => listVendorDocs(id).then((r)        => { if (!r.error) setDocuments(r.data?.data   || []); });

  useEffect(() => { fetchVendor(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    if (activeTab === "addresses"   && addresses.length === 0)    reloadAddresses();
    if (activeTab === "bank"        && bankAccounts.length === 0)  reloadBankAccounts();
    if (activeTab === "documents"   && documents.length === 0)     reloadDocs();
    if (activeTab === "history"     && history.length === 0)
      listVendorHistory(id).then((r) => { if (!r.error) setHistory(r.data?.data?.rows || r.data?.data || []); });
    if (activeTab === "requests"    && relatedRequests.length === 0)
      getData(`t2t/vendors/${id}/related-requests`).then((r) => { if (!r.error) setRelatedRequests(r.data?.data || []); });
    if (activeTab === "performance" && !performance)
      getData(`t2t/vendors/${id}/performance`).then((r) => { if (!r.error) setPerformance(r.data?.data || {}); });
  }, [activeTab, id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <><T2TTopNav title="Vendor Detail" /><div className="t2t-content" style={{ textAlign: "center", padding: 40 }}>Loading...</div></>;
  if (!vendor) return <><T2TTopNav title="Vendor Detail" /><div className="t2t-content" style={{ textAlign: "center", padding: 40, color: "#DC2626" }}>Vendor not found.</div></>;

  const sc    = STATUS_COLORS[vendor.status] || STATUS_COLORS.active;
  const isMock = vendor.sap_vendor_number?.startsWith("MOCK");
  const tabs = [
    { id: "overview",    label: "Overview" },
    { id: "addresses",   label: "Addresses" },
    { id: "bank",        label: "Bank Accounts" },
    { id: "tax",         label: "Tax & Compliance" },
    { id: "documents",   label: "Documents" },
    { id: "history",     label: "History" },
    { id: "requests",    label: "Related Requests" },
    { id: "performance", label: "Performance" },
  ];

  const handleDeleteAddr = async (a) => {
    if (!window.confirm("Delete this address?")) return;
    await deleteVendorAddress(id, a.address_id);
    reloadAddresses();
  };

  const handleDeleteBank = async (b) => {
    if (!window.confirm("Delete this bank account?")) return;
    await deleteVendorBankAccount(id, b.bank_account_id);
    reloadBankAccounts();
  };

  const handleDeleteDoc = async (d) => {
    if (!window.confirm("Delete this document?")) return;
    await deleteVendorDoc(id, d.document_id);
    reloadDocs();
  };

  const handleDownloadDoc = async (d) => {
    const resp = await getDocDownloadUrl(id, d.document_id);
    if (!resp.error) {
      const url = resp.data?.data?.url;
      if (url) window.open(url, "_blank");
    }
  };

  return (
    <>
      <T2TTopNav title={vendor.vendor_number || "Vendor Detail"} />
      <div className="t2t-content">

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => navigate("/t2t-vendors")} style={{ marginBottom: 8 }}>← Back to Vendor List</button>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{vendor.name}</h2>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: sc.bg, color: sc.color }}>{vendor.status?.replace("_", " ").toUpperCase()}</span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "#EFF6FF", color: "#1D4ED8" }}>{vendor.account_group}</span>
              {vendor.vendor_type === "international" && <span>🌐 International</span>}
              <T2TKycBadge vendor={vendor} />
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
              {vendor.vendor_number} {vendor.sap_vendor_number && <span>| SAP: <span style={{ fontFamily: "monospace", color: isMock ? "#D97706" : "#16A34A" }}>{vendor.sap_vendor_number}</span></span>}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              className="t2t-btn t2t-btn-outline t2t-btn-sm"
              onClick={() => navigate(`/t2t-requests?new=vendor_change&vendor_id=${id}`)}
            >
              ✏ Request Change
            </button>
            <button
              className="t2t-btn t2t-btn-outline t2t-btn-sm"
              onClick={() => navigate(`/t2t-requests?new=vendor_extend&vendor_id=${id}`)}
            >
              ＋ Request Extend
            </button>
            <T2TVendorActions vendor={vendor} onActionComplete={fetchVendor} />
          </div>
        </div>

        {/* Tabs */}
        <div className="t2t-tab-group" style={{ marginBottom: 20 }}>
          {tabs.map((t) => (
            <button key={t.id} className={`t2t-tab${activeTab === t.id ? " active" : ""}`} onClick={() => setActiveTab(t.id)}>{t.label}</button>
          ))}
        </div>

        {/* ── Overview ── */}
        {activeTab === "overview" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={CARD}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Basic Information</div>
              {[
                ["Vendor Number", vendor.vendor_number],
                ["Name",          vendor.name],
                ["Name Line 2",   vendor.name_2],
                ["Account Group", vendor.account_group],
                ["Type",          vendor.vendor_type],
                ["Category",      vendor.vendor_category],
                ["Source",        vendor.source],
                ["Created",       vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : "—"],
              ].map(([label, val]) => val && (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12, borderBottom: "1px solid #F8FAFC" }}>
                  <span style={{ color: "#64748B" }}>{label}</span>
                  <span style={{ fontWeight: 600 }}>{val}</span>
                </div>
              ))}
            </div>
            <div>
              <div style={{ ...CARD, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Performance Score</div>
                <div style={{ fontSize: 36, fontWeight: 800, color: (vendor.performance_score || 0) >= 80 ? "#16A34A" : (vendor.performance_score || 0) >= 50 ? "#D97706" : "#DC2626" }}>
                  {vendor.performance_score || 0}<span style={{ fontSize: 14, fontWeight: 400, color: "#94A3B8" }}>/100</span>
                </div>
              </div>
              <T2TSapPushPanel requestId={vendor.created_from_request} vendorId={vendor.vendor_id} sapVendorNumber={vendor.sap_vendor_number} />
            </div>
          </div>
        )}

        {/* ── Addresses ── */}
        {activeTab === "addresses" && (
          <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button className="btn btn-sm btn-primary" onClick={() => setAddrModal("new")}>+ Add Address</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
              {addresses.length === 0 && <div style={EMPTY}>No addresses recorded.</div>}
              {addresses.map((a) => (
                <div key={a.address_id} style={CARD}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "#EFF6FF", color: "#1D4ED8", textTransform: "uppercase" }}>{a.address_type}</span>
                      {a.is_primary === 1 && <span style={{ fontSize: 10, fontWeight: 600, color: "#16A34A" }}>Primary</span>}
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="btn btn-xs btn-outline-secondary" style={{ fontSize: 10, padding: "1px 6px" }} onClick={() => setAddrModal(a)}>Edit</button>
                      <button className="btn btn-xs btn-outline-danger"    style={{ fontSize: 10, padding: "1px 6px" }} onClick={() => handleDeleteAddr(a)}>Del</button>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                    {a.street}<br />{a.city}, {a.state || ""} {a.postal_code}<br />{a.country_code}
                  </div>
                  {a.phone && <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>☎ {a.phone}</div>}
                  {a.email && <div style={{ fontSize: 11, color: "#64748B" }}>✉ {a.email}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Bank Accounts ── */}
        {activeTab === "bank" && (
          <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button className="btn btn-sm btn-primary" onClick={() => setBankModal("new")}>+ Add Bank Account</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
              {bankAccounts.length === 0 && <div style={EMPTY}>No bank accounts recorded.</div>}
              {bankAccounts.map((b) => (
                <div key={b.bank_account_id} style={CARD}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{b.bank_name}</span>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: b.is_verified ? "#F0FDF4" : "#FFF7ED", color: b.is_verified ? "#16A34A" : "#D97706" }}>
                        {b.is_verified ? "Verified" : "Unverified"}
                      </span>
                      <button className="btn btn-xs btn-outline-secondary" style={{ fontSize: 10, padding: "1px 6px" }} onClick={() => setBankModal(b)}>Edit</button>
                      <button className="btn btn-xs btn-outline-danger"    style={{ fontSize: 10, padding: "1px 6px" }} onClick={() => handleDeleteBank(b)}>Del</button>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                    <div>A/C: <span style={{ fontFamily: "monospace" }}>{"****" + (b.account_number || "").slice(-4)}</span></div>
                    {b.ifsc_code  && <div>IFSC: <span style={{ fontFamily: "monospace" }}>{b.ifsc_code}</span></div>}
                    {b.swift_code && <div>SWIFT: <span style={{ fontFamily: "monospace" }}>{b.swift_code}</span></div>}
                    {b.iban       && <div>IBAN: <span style={{ fontFamily: "monospace" }}>{b.iban}</span></div>}
                    <div>Type: {b.account_type} | Currency: {b.currency || "INR"}</div>
                  </div>
                  {b.is_primary === 1 && <div style={{ fontSize: 10, color: "#16A34A", fontWeight: 600, marginTop: 6 }}>Primary Payment Account</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Tax & Compliance ── */}
        {activeTab === "tax" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={CARD}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Tax Details</div>
              {vendor.vendor_type === "domestic" ? (
                <div style={{ fontSize: 12, lineHeight: 2 }}>
                  <div>PAN: <strong style={{ fontFamily: "monospace" }}>{vendor.pan_number || "—"}</strong> {vendor.pan_verified && <span style={{ color: "#16A34A" }}>✅</span>}</div>
                  <div>GSTIN: <strong style={{ fontFamily: "monospace" }}>{vendor.gstin || "—"}</strong> {vendor.gstin_verified && <span style={{ color: "#16A34A" }}>✅</span>}</div>
                </div>
              ) : (
                <div style={{ fontSize: 12, lineHeight: 2 }}>
                  <div>VAT: <strong>{vendor.vat_number || "—"}</strong></div>
                  <div>Tax ID: <strong>{vendor.tax_id || "—"}</strong></div>
                  <div>SWIFT: <strong style={{ fontFamily: "monospace" }}>{vendor.swift_code || "—"}</strong></div>
                </div>
              )}
            </div>
            <T2TVerificationPanel requestId={vendor.created_from_request} vendorType={vendor.vendor_type} stage="approver" />
          </div>
        )}

        {/* ── Documents ── */}
        {activeTab === "documents" && (
          <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button className="btn btn-sm btn-primary" onClick={() => setDocModal(true)}>+ Upload Document</button>
            </div>
            {documents.length === 0 && <div style={EMPTY}>No documents uploaded.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {documents.map((d) => (
                <div key={d.document_id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", ...CARD }}>
                  <span style={{ fontSize: 20 }}>📄</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{d.document_name || d.document_type}</div>
                    <div style={{ fontSize: 10, color: "#64748B" }}>{d.document_type} | {d.mime_type} | {Math.round((d.file_size_bytes || 0) / 1024)} KB</div>
                  </div>
                  <T2TDocExpiryBadge expiryDate={d.expiry_date} />
                  {d.is_verified === 1 && <span style={{ fontSize: 10, fontWeight: 600, color: "#16A34A" }}>✅ Verified</span>}
                  <button className="btn btn-xs btn-outline-secondary" style={{ fontSize: 11 }} onClick={() => handleDownloadDoc(d)}>Download</button>
                  <button className="btn btn-xs btn-outline-danger"    style={{ fontSize: 11 }} onClick={() => handleDeleteDoc(d)}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── History / Audit ── */}
        {activeTab === "history" && (
          <div>
            {history.length === 0 && <div style={EMPTY}>No history events.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {history.map((h) => (
                <div key={h.event_id || h.id} style={{ display: "flex", gap: 12, padding: "10px 14px", ...CARD, fontSize: 12 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#36B82A", marginTop: 5, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{(h.event_type || h.action_type || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</div>
                    {(h.notes || h.description) && <div style={{ color: "#64748B", marginTop: 2 }}>{h.notes || h.description}</div>}
                    {h.actor_name && <div style={{ color: "#94A3B8", fontSize: 11, marginTop: 1 }}>by {h.actor_name}</div>}
                    <div style={{ color: "#94A3B8", marginTop: 2, fontSize: 10 }}>{h.created_at ? new Date(h.created_at).toLocaleString() : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Related Requests ── */}
        {activeTab === "requests" && (
          <div>
            {relatedRequests.length === 0 && <div style={EMPTY}>No related workflow requests.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {relatedRequests.map((r) => (
                <div key={r.id} onClick={() => navigate(`/t2t-request/${r.id}`)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", ...CARD, cursor: "pointer", fontSize: 12 }}>
                  <span style={{ fontWeight: 700, color: "var(--t2t-primary)" }}>{r.request_ref || r.id}</span>
                  <span style={{ flex: 1 }}>{r.title || r.workflow_name || "Vendor Request"}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
                    background: r.status === "approved" ? "#F0FDF4" : r.status === "rejected" ? "#FEF2F2" : "#FFF7ED",
                    color:      r.status === "approved" ? "#16A34A" : r.status === "rejected" ? "#DC2626" : "#D97706" }}>
                    {r.status?.toUpperCase()}
                  </span>
                  <span style={{ color: "#94A3B8", fontSize: 10 }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Performance ── */}
        {activeTab === "performance" && (
          <div>
            {!performance ? (
              <div style={EMPTY}>Loading performance data...</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={CARD}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Score Breakdown</div>
                  {[
                    { label: "On-Time Delivery",       weight: "35%", value: performance.delivery_score },
                    { label: "Invoice Accuracy",        weight: "25%", value: performance.invoice_score },
                    { label: "Responsiveness",          weight: "20%", value: performance.responsiveness_score },
                    { label: "Documentation Quality",   weight: "10%", value: performance.documentation_score },
                    { label: "Compliance History",      weight: "10%", value: performance.compliance_score },
                  ].map((item) => (
                    <div key={item.label} style={{ marginBottom: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                        <span>{item.label} <span style={{ color: "#94A3B8" }}>({item.weight})</span></span>
                        <span style={{ fontWeight: 700 }}>{item.value ?? "—"}</span>
                      </div>
                      <div style={{ height: 6, background: "#E2E8F0", borderRadius: 4 }}>
                        <div style={{ height: "100%", width: `${item.value || 0}%`, background: (item.value || 0) >= 80 ? "#16A34A" : (item.value || 0) >= 50 ? "#D97706" : "#DC2626", borderRadius: 4 }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ ...CARD, textAlign: "center", marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: "#64748B", marginBottom: 4 }}>Overall Score</div>
                    <div style={{ fontSize: 48, fontWeight: 800, color: (vendor.performance_score || 0) >= 80 ? "#16A34A" : (vendor.performance_score || 0) >= 50 ? "#D97706" : "#DC2626" }}>
                      {vendor.performance_score || 0}
                    </div>
                  </div>
                  <T2TVendorInviteButton />
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Modals */}
      {addrModal && (
        <AddressModal
          vendorId={id}
          existing={addrModal === "new" ? null : addrModal}
          onSaved={reloadAddresses}
          onClose={() => setAddrModal(null)}
        />
      )}
      {bankModal && (
        <BankModal
          vendorId={id}
          existing={bankModal === "new" ? null : bankModal}
          onSaved={reloadBankAccounts}
          onClose={() => setBankModal(null)}
        />
      )}
      {docModal && (
        <DocUploadModal
          vendorId={id}
          onSaved={reloadDocs}
          onClose={() => setDocModal(false)}
        />
      )}
    </>
  );
};

export default T2TVendorDetail;
